import type { MyAnalyticsNotificationActivityFeedItem } from '../service/ActivityFeedItem';
import {
    ActivityFeedCardDetails,
    HeroActionProperties,
    ActivityCardLayoutType,
    formatBoldMarkdownString,
} from './feedCardPresentationDetails';
import loc, { format } from 'owa-localize';
import { ActivityType } from '../service/ActivityFeedStorageItem';
import { ControlIcons } from 'owa-control-icons';
import {
    activityFeedMyAnalyticsDoneHeroActionText,
    activityFeedMyAnalyticsNotATaskHeroActionText,
    activityFeedMyAnalyticsNotAFollowUpHeroActionText,
    activityFeedMyAnalyticsTaskNotificationTitle,
    activityFeedMyAnalyticsFollowUpNotificationTitle,
} from './activityFeedCard.locstring.json';
import { formatPastRelativeDateTime, parse } from 'owa-datetime';
import { observableNow } from 'owa-observable-datetime';
import { getUserConfiguration } from 'owa-session-store';
import { convertRestIdToEwsId } from 'owa-identifiers';

export function getTaskInsightFeedCard(
    item: MyAnalyticsNotificationActivityFeedItem
): ActivityFeedCardDetails {
    if (item.payload.Inference.Observations) {
        const observations = item.payload.Inference.Observations[0];
        const senderInfo = observations.Value.Requesters.map(function (requester) {
            return { name: requester.DisplayName, emailAddress: requester.MailAddress };
        });
        let myAnalyticsTitle =
            item.type == ActivityType.FollowUpNotificationInsight
                ? format(
                      loc(activityFeedMyAnalyticsFollowUpNotificationTitle),
                      getDaysAgo(observations.Value.SourceReceivedDateTime)
                  )
                : format(
                      loc(activityFeedMyAnalyticsTaskNotificationTitle),
                      getDaysAgo(observations.Value.SourceReceivedDateTime)
                  );
        myAnalyticsTitle = item.isSeen
            ? myAnalyticsTitle
            : formatBoldMarkdownString(myAnalyticsTitle);
        let ewsId: string = convertRestIdToEwsId(observations.Value.SourceMailId as string);

        return {
            senderInfo: senderInfo,
            messageItemId: ewsId,
            cardTitle: myAnalyticsTitle,
            cardSubTitle1: observations.Value.ToDoProperties.description,
            cardSubTitle2: observations.Value.SourceMailSubject,
            timeStamp: item.timestamp,
            isSeen: item.isSeen,
            cardType: ActivityCardLayoutType.MultiTitleActivityCard,
            activityType: ActivityType.TaskNotificationInsight,
            cardIcon: ControlIcons.AnalyticsLogo,
            heroButtonProperties: getHeroActionsFromPayload(item),
        } as ActivityFeedCardDetails;
    }
    return {} as ActivityFeedCardDetails;
}

function getHeroActionsFromPayload(
    item: MyAnalyticsNotificationActivityFeedItem
): HeroActionProperties[] {
    const taskSuggestionUrl = `https://outlook.office.com/weveb2/api/v1.0/me/insights('${item.payload.Id}')/execute`;
    var doneTaskBody = item.payload.Suggestion.SuggestedActions.filter(
        action => action.Name == 'MarkTaskAsDone'
    )[0];
    var notATaskBody = item.payload.Suggestion.SuggestedActions.filter(
        action => action.Name == 'MarkAsNotATask'
    )[0];
    const markAsDoneApiConfig = {
        buttonTitle: loc(activityFeedMyAnalyticsDoneHeroActionText),
        buttonActionServiceConfig: {
            url: taskSuggestionUrl,
            method: 'POST',
            body: JSON.stringify({
                action: {
                    TaskSuggestionId: doneTaskBody.TaskSuggestionId,
                    Name: doneTaskBody.Name,
                },
            }),
        },
        heroActionName: 'MarkAsDone',
    } as HeroActionProperties;

    const notATaskApiConfig = {
        buttonTitle:
            item.type == ActivityType.TaskNotificationInsight
                ? loc(activityFeedMyAnalyticsNotATaskHeroActionText)
                : loc(activityFeedMyAnalyticsNotAFollowUpHeroActionText),
        buttonActionServiceConfig: {
            url: taskSuggestionUrl,
            method: 'POST',
            body: JSON.stringify({
                action: {
                    TaskSuggestionId: notATaskBody.TaskSuggestionId,
                    Name: notATaskBody.Name,
                },
            }),
        },
        heroActionName: 'MarkNotATask',
    } as HeroActionProperties;
    return [markAsDoneApiConfig, notATaskApiConfig];
}

function getDaysAgo(date: string) {
    const suggestionDate = parse(getUserConfiguration().UserOptions.TimeZone, date);
    return formatPastRelativeDateTime(suggestionDate, observableNow());
}
